/* Built In Imports */
import { useRouter } from 'next/router';
import { useState } from 'react';

/* External Imports */
import {
    Box,
    Button, Collapse, Flex,
    FormControl,
    FormErrorMessage,
    FormLabel, Radio, RadioGroup, Stack, Textarea
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { FaTimes } from 'react-icons/fa';
import * as Yup from 'yup';

/* Internal Imports */
/* Components */
import InputUI from '@components/Elements/InputUI';
import { useDonateFormContext } from '@store/donateFormContext';

/* Services */
import { submitOfflineForm } from 'services/donationService';

/**
 * Renders the Placeholder Donate Offline Form Section component
 *
 * @param section.section
 * @param {object} section - Data for section
 * @param {string} region - Region for the section
 * @param {string} lang - Language for the section
 * @param {boolean} isLeftMenu - Responsive left Navbar for the section
 * @param section.region
 * @param section.lang
 * @param section.isLeftMenu
 * @param section.pType
 * @param section.pageName
 * @returns {ReactElement} Place holder Donate Offline Form component
 */
const PlaceholderDonateOfflineForm = ({
  section,
  region,
  lang,
  isLeftMenu,
  pType,
  pageName,
}) => {
  const [err, setErr] = useState('');  
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { formActive, toggleFormActive } = useDonateFormContext();

  const ValidationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, 'First Name is invalid')
      .matches(/^([a-zA-Z ])+$/u, {
        message: 'First Name is invalid',
      })
      .max(100, 'First Name is invalid')
      .required('First Name is required'),
    lastName: Yup.string()
      // .min(2, 'Last Name Invalid')
      .matches(/^([a-zA-Z ])+$/u, {
        message: 'Last Name is invalid',
      })
      .max(100, 'Last Name Invalid')
      .required('Last Name is required'),
    email: Yup.string()
      .matches(/^[a-zA-Z]+[a-zA-Z0-9._]+@[a-zA-Z]+\.[a-zA-Z.]{2,15}$/, {
        message: 'Email is invalid',
      })
      .required('Email is required'),
    phoneNumber: Yup.string()
      .matches(/^([0-9]{10,15})+$/u, 'Invalid phone number')
      .required('Phone number is required'),
    comments: Yup.string(),
  });

  const onSubmit = async (values, actions) => {
    let formValues = values;
    formValues.Initiative = 'ISO';
    // formValues.UID = 1311;
    formValues.fullName = `${formValues.firstName} ${formValues.lastName}`;
    actions.setSubmitting(true);
    setLoading(true);
    let formResponse = await submitOfflineForm(formValues);
    if (formResponse?.statusCode === '1') {
      router.push(`/${region}/${lang}/donation/offline-thank-you`);
    } else {
      router.push(`/${region}/${lang}/donation/offline-failure`);
    }
  };

  if (!formActive) {
    return null;
  }

  return (
    <Collapse in={formActive} animateOpacity>
      <Box borderBottom="1px solid #dfd6c4">
        <Box
          fontSize={'24px'}
          marginRight="30px"
          marginTop="20px"
          float="right"
          color={'black'}
          fontWeight="100"
          onClick={toggleFormActive}
        >
          <FaTimes cursor="pointer" />
        </Box>
        <Box
          maxW={{ base: '302px', sm: '480px' }}
          w="100%"
          textAlign="center"
          py="20px"
          mx="auto"
        >
          <Box as="h3" fontSize="28px" fontFamily="FedraSansStd-A-medium">
            Donate Offline
          </Box>
          <Box
            as="p"
            fontFamily="FedraSansStd-book"
            fontSize="18px"
            pb="9"
            pt="2"
          >
            Currently For Indian Residents Only
          </Box>
          <Flex mb="20px" flexDir="column" w="full">
            <Formik
              initialValues={{
                paymentMode: 'regular',
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                comments: '',
              }}
              validationSchema={ValidationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                errors,
                isSubmitting,
                isValid
              }) => (
                <Form
                  onSubmit={handleSubmit}
                  style={{
                    display: 'grid',
                    rowGap: '25px',
                    padding: '15px 10px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box display="grid" placeContent="left" gridRowGap="15px">
                    <Box display="flex">
                      <Field name="paymentMode">
                        {({ field, form }) => {
                          const { onChange, ...rest } = field;
                          return (
                            <FormControl
                              isRequired
                              isInvalid={
                                !!form.errors['paymentMode'] &&
                                !!form.touched['paymentMode']
                              }
                            >
                              <Box display="flex" gridGap={10}>
                                <RadioGroup {...rest}>
                                  <Stack
                                    direction="column"
                                    fontFamily="FedraSansStd-book"
                                    mb="20px"
                                  >
                                    <Radio
                                      value="Cheque/DD"
                                      key="regular"
                                      colorScheme="orange"
                                      onChange={onChange}
                                    >
                                      Cheque/DD
                                    </Radio>
                                    <Radio
                                      value="ach"
                                      key="ach"
                                      colorScheme="orange"
                                      onChange={onChange}
                                    >
                                      Regular - ACH
                                    </Radio>
                                    <Radio
                                      value="kind"
                                      key="kind"
                                      colorScheme="orange"
                                      onChange={onChange}
                                    >
                                      In-kind Donation
                                    </Radio>
                                  </Stack>
                                </RadioGroup>
                              </Box>
                              <FormErrorMessage>
                                {form.errors['paymentMode']}
                              </FormErrorMessage>
                            </FormControl>
                          );
                        }}
                      </Field>
                    </Box>
                  </Box>
                  <Flex
                    width={'100%'}
                    justifyContent="space-between"
                    flexDir={{ md: 'row', sm: 'column', base: 'column' }}
                    flexWrap="wrap"
                    gridRowGap={30}
                    m="0 auto"
                  >
                    <Field name="firstName">
                      {({ field, form }) => (
                        <FormControl
                          isRequired
                          isInvalid={
                            form.errors.firstName && form.touched.firstName
                          }
                          display="flex"
                          flexDir="column"
                          maxWidth={{ md: '195px', sm: '460px', base: '300px' }}
                          m={{ md: '0 15px 0 0', sm: '0', base: '0' }}
                          fontFamily="FedraSansStd-book"
                        >
                          <FormLabel
                            //  w={{ md: '215px', sm: '460px', base: '300px' }}
                            w="100%"
                            // m={{ md: '0 15px 0 0', sm: '0', base: '0' }}
                            htmlFor="firstName"
                            color="#676055"
                            fontWeight="600"
                          >
                            First Name
                          </FormLabel>
                          <InputUI
                            // wd={{ md: '215px', sm: '460px', base: '300px' }}
                            // m={{ md: '0 15px 0 0', sm: '0', base: '0' }}
                            field={field}
                            id="firstName"
                            placeholder="First Name"
                            color="#000"
                            onChange={handleChange}
                          />
                          <FormErrorMessage justifyContent="left">
                            {form.errors.firstName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field
                      name="lastName"
                      // width={{ md: '215px', sm: '460px', base: '300px' }}
                      // m={{ md: '0 30px 0 0', sm: '0', base: '0' }}
                    >
                      {({ field, form }) => (
                        <FormControl
                          isRequired
                          isInvalid={
                            form.errors.lastName && form.touched.lastName
                          }
                          display="flex"
                          flexDir="column"
                          maxWidth={{ md: '215px', sm: '460px', base: '300px' }}
                          m={{ md: '0 0 0 15px', sm: '0', base: '0' }}
                          fontFamily="FedraSansStd-book"
                        >
                          <FormLabel
                            htmlFor="lastName"
                            color="#676055"
                            fontWeight="600"
                          >
                            Last Name
                          </FormLabel>
                          <InputUI
                            // wd={{ md: '215px', sm: '460px', base: '300px' }}
                            // m={{ md: '0 0 0 15px', sm: '0', base: '0' }}
                            color="#000"
                            field={field}
                            id="lastName"
                            placeholder="Last Name"
                            onChange={handleChange}
                          />
                          <FormErrorMessage justifyContent="left">
                            {form.errors.lastName}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Flex>
                  <Field name="email">
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        isInvalid={form.errors.email && form.touched.email}
                      >
                        <FormLabel
                          htmlFor="email"
                          color="#676055"
                          fontWeight="600"
                          fontFamily="FedraSansStd-book"
                        >
                          Email
                        </FormLabel>
                        <InputUI
                          color="#000"
                          field={field}
                          id="email"
                          placeholder="Email"
                          onChange={handleChange}
                        />
                        <FormErrorMessage justifyContent="left">
                          {form.errors.email}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Box display="grid">
                    <Flex>
                      <Field
                        name="phoneNumber"
                        color="#676055"
                        fontWeight="600"
                      >
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.phoneNumber &&
                              form.touched.phoneNumber
                            }
                            isRequired
                          >
                            <FormLabel
                              htmlFor="phoneCountryCode"
                              color="#676055"
                              fontWeight="600"
                              fontFamily="FedraSansStd-book"
                            >
                              Mobile Number
                            </FormLabel>
                            <InputUI
                              field={field}
                              color="#000"
                              id="phoneNumber"
                              placeholder="Mobile"
                              // w="300px"
                              maxLength="10"
                              name="phoneNumber"
                              onChange={handleChange}
                            />
                            <FormErrorMessage justifyContent="left">
                              {form.errors.phoneNumber}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                  </Box>
                  <Field name="comments">
                    {({ field, form }) => (
                      <FormControl
                        maxWidth={{ md: '460px', sm: '460px', base: '300px' }}
                        fontFamily="FedraSansStd-book"
                      >
                        <FormLabel
                            //  w={{ md: '215px', sm: '460px', base: '300px' }}
                          w="100%"
                            // m={{ md: '0 15px 0 0', sm: '0', base: '0' }}
                          htmlFor="firstName"
                          color="#676055"
                          fontWeight="600"
                          >
                          Comments
                        </FormLabel>
                        <Textarea
                          variant="flushed"
                          placeholder="Comments"
                          color="#000"
                          id="comments"
                          onChange={handleChange}
                          _placeholder={{ color: 'grey', fontSize: '12px' }}                          
                        />
                        <FormErrorMessage justifyContent="left">
                          {form.errors.comments}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* <FormControl>
                  <Textarea variant="flushed" placeholder="Comments" />
                </FormControl>
                <FormErrorMessage justifyContent="center">
                  {form.errors.phoneNumber}
                </FormErrorMessage> */}
                  {/* </Flex> */}
                  <Box display="grid" placeContent="center">
                    <Button
                      type="submit"
                      mb="20px"
                      justifyContent="center"
                      alignItems="center"
                      fontWeight={500}
                      fontFamily="FedraSansStd-medium"
                      fontSize={'18px'}
                      bgColor="#cf4520"
                      textAlign="center"
                      padding="6px 40px"
                      borderRadius="20px"
                      color="whiteAlpha.900"
                      textDecoration="none"
                      _hover={{
                        bg: 'blue.50',
                      }}
                      disabled={isValid ? (loading ? true : false) : true}
                    >
                      {loading ? 'Submitting..' : 'Continue'}
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          </Flex>
        </Box>
      </Box>
    </Collapse>
  );
};

export default PlaceholderDonateOfflineForm;
